// Content-free retry responsibility. Each owner/operation is a separate record;
// native readwrite transactions serialize concurrent tabs, never whole-map writes.
export const CAMERA_STREAM_CLEANUP_STORAGE_KEY = 'kata.cameraStreamCleanup.v1';
const DB_NAME = 'kata.cameraStreamCleanup.v2';
const STORE = 'intents';
const safeId = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,80}$/.test(value) ? value : null;
const failure = () => Object.assign(new Error('Durable camera recovery storage is unavailable. Recovery is unconfirmed; retry after enabling browser storage.'), { code: 'CAMERA_STORAGE_UNAVAILABLE' });

export function createCameraStreamCleanupLedger({ indexedDB, storage, now = Date.now } = {}) {
  // Connections are closed after each unit so upgrades/test disposal cannot leak.
  let factory = indexedDB;
  function open() {
    return new Promise((resolve, reject) => {
      if (factory === undefined) factory = typeof window !== 'undefined' ? window.indexedDB : null;
      if (!factory) { reject(failure()); return; }
      let request;
      try { request = factory.open(DB_NAME, 1); } catch (_) { reject(failure()); return; }
      request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: ['owner', 'key'] });
      request.onerror = () => reject(failure());
      request.onblocked = () => reject(failure());
      request.onsuccess = () => resolve(request.result);
    });
  }
  async function unit(mode, action) {
    const db = await open();
    try {
      return await new Promise((resolve, reject) => {
        let result;
        const tx = mode === 'readwrite' ? db.transaction(STORE, mode, { durability: 'strict' }) : db.transaction(STORE, mode);
        tx.oncomplete = () => resolve(result);
        tx.onabort = tx.onerror = () => reject(failure());
        action(tx.objectStore(STORE), value => { result = value; });
      });
    } catch (_) { throw failure(); } finally { db.close(); }
  }
  const normalize = ref => ({ operationId: safeId(ref?.operationId), sessionId: safeId(ref?.sessionId) });
  const keyOf = ref => ref.operationId ? `operation:${ref.operationId}` : `session:${ref.sessionId}`;
  let migration;
  function ready() {
    if (!migration) migration = unit('readwrite', (store, done) => {
      const marker = store.get(['', 'legacy-import']);
      marker.onsuccess = () => {
        if (marker.result) { done(true); return; }
        try {
          const legacyStorage = storage === undefined ? (typeof window !== 'undefined' ? window.localStorage : null) : storage;
          const all = JSON.parse(legacyStorage?.getItem(CAMERA_STREAM_CLEANUP_STORAGE_KEY) || '{}');
          if (!all || typeof all !== 'object' || Array.isArray(all)) throw failure();
          Object.entries(all).forEach(([owner, rows]) => {
            if (!owner || !Array.isArray(rows)) throw failure();
            rows.forEach(raw => {
              const clean = normalize(raw);
              if (!clean.operationId && !clean.sessionId) throw failure();
              const key = keyOf(clean);
              const request = store.get([owner, key]);
              request.onsuccess = () => {
                if (!request.result) store.put({ owner, key, ...clean, since: Number(raw.since) || 0,
                  autoRounds: Math.max(0, Number(raw.autoRounds) || 0) });
              };
            });
          });
          store.put({ owner: '', key: 'legacy-import' });
          done(true);
        } catch (_) { store.transaction.abort(); }
      };
    }).catch(error => { migration = null; throw error; });
    return migration;
  }
  async function entries(owner) {
    if (!owner) return [];
    await ready();
    return unit('readonly', (store, done) => {
      const request = store.getAll();
      request.onsuccess = () => done(request.result.filter(row => row.owner === owner && row.settled !== true).map(({ owner: ignored, key, ...entry }) => entry));
    });
  }
  async function mutate(owner, ref, kind) {
    const clean = normalize(ref);
    if (!owner || (!clean.operationId && !clean.sessionId)) return;
    await ready();
    return unit('readwrite', (store, done) => {
      const request = store.getAll();
      request.onsuccess = () => {
        // Operation identity wins over session identity: never erase another O.
        const existing = request.result.find(row => row.owner === owner && (clean.operationId
          ? row.operationId === clean.operationId : row.sessionId === clean.sessionId));
        if (kind === 'delete') {
          // Exact-operation consumers supply an explicit session binding (null
          // for prearrival). Recheck inside this serialized transaction: another
          // connection may have associated a session since certificate validation.
          if (clean.operationId && Object.prototype.hasOwnProperty.call(ref, 'sessionId')
            && existing?.sessionId && existing.sessionId !== clean.sessionId) {
            store.transaction.abort();
            return;
          }
          store.put({ ...(existing || { owner, key: keyOf(clean), ...clean, since: now(), autoRounds: 0 }), settled: true });
          done({ durable: true, ...clean });
          return;
        }
        if (existing?.settled === true) {
          if (kind === 'put') store.transaction.abort();
          return;
        }
        if (kind === 'round' && !existing) return;
        const row = existing || { owner, key: keyOf(clean), ...clean, since: now(), autoRounds: 0 };
        if (kind === 'round') row.autoRounds += 1;
        else row.sessionId = clean.sessionId || row.sessionId;
        store.put(row);
        done({ durable: true, ...clean });
      };
    });
  }
  return { entries, owe: (owner, ref) => mutate(owner, ref, 'put'), discharge: (owner, ref) => mutate(owner, ref, 'delete'), noteAutoRound: (owner, ref) => mutate(owner, ref, 'round') };
}
export const cameraStreamCleanupLedger = createCameraStreamCleanupLedger();
